import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ActorRef } from '@/features/users/api'
import type { ListMeta } from '@/lib/api'
import { useApi } from '@/lib/api-context'
import { useIdempotencyKey } from '@/lib/use-idempotency-key'

/**
 * Os apps de integração da clínica ativa, os webhooks e as entregas (docs/api/admin.md, "Integrações";
 * docs/areas/integracoes.md). Os nomes de campo são os de
 * `apps/api/supabase/functions/admin/integrations/schemas.ts`; o vocabulário (escopos, eventos) vem de
 * `GET /admin/integrations/catalog`, nunca repetido aqui.
 */

export type Scope =
  | 'patients:read'
  | 'patients:write'
  | 'anamneses:read'
  | 'anamneses:write'
  | 'anamneses:answers'
  | 'documents:read'
  | 'documents:write'
  | 'catalog:read'
  | 'webhooks:write'

export interface Integration {
  id: string
  name: string
  description: string | null
  scopes: Scope[]
  /** Os 8 primeiros caracteres depois de `wx_`. */
  keyPrefix: string
  /** Até quando a chave anterior vale, nos 15 minutos depois de regenerar. */
  previousKeyValidUntil: string | null
  status: 'active' | 'revoked'
  lastUsedAt: string | null
  createdAt: string
  createdBy: ActorRef
  updatedAt: string
  revokedAt: string | null
  revokedBy: ActorRef
  webhooks: { total: number; active: number }
  usage: { requests24h: number; errors24h: number }
  deliveries: { pending: number; failed: number; succeeded24h: number }
}

export interface IntegrationListMeta {
  total: number
  /** Quantos apps ativos a clínica pode ter (10, P-INT-05). */
  limit: number
  revokedCount: number
}

export interface Webhook {
  id: string
  integrationId: string
  url: string
  description: string | null
  /** `['*']`: todos os eventos que o app vê. */
  events: string[]
  status: 'active' | 'disabled'
  /** `failing`: a Westhetics desligou depois de 72 horas só de falhas. */
  disabledReason: 'manual' | 'failing' | null
  disabledAt: string | null
  failingSince: string | null
  lastDeliveryAt: string | null
  lastSuccessAt: string | null
  hasPreviousSecret: boolean
  previousSecretValidUntil: string | null
  deliveries: { pending: number; failed: number }
  createdAt: string
  updatedAt: string
}

export interface Delivery {
  id: string
  webhookId: string
  eventId: string
  eventType: string
  status: 'pending' | 'succeeded' | 'failed'
  attempts: number
  nextAttemptAt: string | null
  lastAttemptAt: string | null
  responseStatus: number | null
  durationMs: number | null
  error: string | null
  createdAt: string
}

export type DeliveryFilter = 'all' | 'failed' | 'pending'

export interface DeliveriesMeta extends ListMeta {
  counts: Record<DeliveryFilter, number>
}

export interface IntegrationRequest {
  id: number
  channel: 'api' | 'mcp'
  method: string
  /** O molde da rota (`/v1/patients/:id`) ou o nome da ferramenta do MCP. */
  route: string
  status: number
  durationMs: number
  requestId: string | null
  occurredAt: string
}

export interface WebhookSecret {
  webhookId: string
  secret: string
  previousSecret: string | null
  previousSecretValidUntil: string | null
}

/** O vocabulário da tela, das mesmas listas da API e do banco. */
export interface IntegrationsCatalog {
  scopes: { scope: Scope; label: string; description: string }[]
  resources: {
    resource: 'patients' | 'anamneses' | 'documents' | 'catalog'
    label: string
    read: Scope
    write: Scope | null
  }[]
  eventGroups: { resource: string; label: string; scope: Scope }[]
  events: { type: string; resource: string; description: string }[]
}

export interface IntegrationInput {
  name: string
  description: string | null
  scopes: Scope[]
}

export interface WebhookInput {
  url: string
  description: string | null
  events: string[]
}

export interface WebhookChange {
  url?: string
  description?: string | null
  events?: string[]
  status?: 'active' | 'disabled'
}

/** A chave inteira aparece só nesta resposta; repetida pela idempotência, vem nula. */
export interface IntegrationWithKey {
  integration: Integration
  key: string | null
}

export interface WebhookWithSecret {
  webhook: Webhook
  secret: string | null
}

export const integrationKeys = {
  all: ['integrations'] as const,
  catalog: ['integrations', 'catalog'] as const,
  list: ['integrations', 'list'] as const,
  detail: (id: string) => ['integrations', 'detail', id] as const,
  requests: (id: string) => ['integrations', 'requests', id] as const,
  webhooks: (id: string) => ['integrations', 'webhooks', id] as const,
  deliveries: (id: string, params: object) => ['integrations', 'deliveries', id, params] as const,
}

export function useIntegrationsCatalog() {
  const api = useApi()
  return useQuery({
    queryKey: integrationKeys.catalog,
    queryFn: () => api.get<IntegrationsCatalog>('/admin/integrations/catalog'),
    staleTime: Infinity,
  })
}

export function useIntegrations() {
  const api = useApi()
  return useQuery({
    queryKey: integrationKeys.list,
    queryFn: () => api.list<Integration, IntegrationListMeta>('/admin/integrations'),
  })
}

export function useIntegration(id: string | undefined) {
  const api = useApi()
  return useQuery({
    queryKey: integrationKeys.detail(id ?? ''),
    queryFn: () => api.get<Integration>(`/admin/integrations/${id}`),
    enabled: Boolean(id),
  })
}

export function useCreateIntegration() {
  const api = useApi()
  const queryClient = useQueryClient()
  const idempotency = useIdempotencyKey()
  return useMutation({
    mutationFn: (input: IntegrationInput) =>
      api.post<IntegrationWithKey>('/admin/integrations', input, {
        idempotencyKey: idempotency.keyFor(input),
      }),
    onSuccess: (created) => {
      idempotency.reset()
      queryClient.setQueryData(integrationKeys.detail(created.integration.id), created.integration)
      return queryClient.invalidateQueries({ queryKey: integrationKeys.list })
    },
  })
}

function useIntegrationMutation<I, O>(
  id: string,
  request: (input: I) => Promise<O>,
  pick?: (output: O) => Integration,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: request,
    onSuccess: (output) => {
      if (pick) queryClient.setQueryData(integrationKeys.detail(id), pick(output))
      return queryClient.invalidateQueries({ queryKey: integrationKeys.list })
    },
  })
}

export function useUpdateIntegration(id: string) {
  const api = useApi()
  return useIntegrationMutation(
    id,
    (input: IntegrationInput) => api.put<Integration>(`/admin/integrations/${id}`, input),
    (integration) => integration,
  )
}

export function useRegenerateKey(id: string) {
  const api = useApi()
  return useIntegrationMutation(
    id,
    (justification: string) =>
      api.post<IntegrationWithKey & { emailsSent: number }>(
        `/admin/integrations/${id}/regenerate-key`,
        { justification },
      ),
    (output) => output.integration,
  )
}

export function useRevokeIntegration(id: string) {
  const api = useApi()
  return useIntegrationMutation(
    id,
    (justification: string | null) =>
      api.post<Integration>(
        `/admin/integrations/${id}/revoke`,
        justification ? { justification } : {},
      ),
    (integration) => integration,
  )
}

export function useIntegrationRequests(id: string, enabled = true) {
  const api = useApi()
  return useQuery({
    queryKey: integrationKeys.requests(id),
    queryFn: () => api.get<IntegrationRequest[]>(`/admin/integrations/${id}/requests`),
    enabled,
  })
}

export function useWebhooks(id: string) {
  const api = useApi()
  return useQuery({
    queryKey: integrationKeys.webhooks(id),
    queryFn: () => api.get<Webhook[]>(`/admin/integrations/${id}/webhooks`),
  })
}

/** Toda mudança de webhook refaz a lista dele e os números do app (webhooks ativos, entregas). */
function useWebhookMutation<I, O>(integrationId: string, request: (input: I) => Promise<O>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: request,
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: integrationKeys.webhooks(integrationId) }),
        queryClient.invalidateQueries({ queryKey: integrationKeys.detail(integrationId) }),
        queryClient.invalidateQueries({ queryKey: ['integrations', 'deliveries', integrationId] }),
      ]),
  })
}

export function useCreateWebhook(integrationId: string) {
  const api = useApi()
  const idempotency = useIdempotencyKey()
  const mutation = useWebhookMutation(integrationId, (input: WebhookInput) =>
    api.post<WebhookWithSecret>(`/admin/integrations/${integrationId}/webhooks`, input, {
      idempotencyKey: idempotency.keyFor(input),
    }),
  )
  return { ...mutation, resetKey: idempotency.reset }
}

export function useUpdateWebhook(integrationId: string) {
  const api = useApi()
  return useWebhookMutation(integrationId, (p: { id: string; change: WebhookChange }) =>
    api.put<Webhook>(`/admin/integrations/webhooks/${p.id}`, p.change),
  )
}

export function useDeleteWebhook(integrationId: string) {
  const api = useApi()
  return useWebhookMutation(integrationId, (webhookId: string) =>
    api.delete(`/admin/integrations/webhooks/${webhookId}`),
  )
}

export function useRotateWebhookSecret(integrationId: string) {
  const api = useApi()
  return useWebhookMutation(integrationId, (webhookId: string) =>
    api.post<WebhookWithSecret>(`/admin/integrations/webhooks/${webhookId}/rotate-secret`),
  )
}

export function useTestWebhook(integrationId: string) {
  const api = useApi()
  return useWebhookMutation(integrationId, (webhookId: string) =>
    api.post<Delivery>(`/admin/integrations/webhooks/${webhookId}/test`),
  )
}

/** Revelar o segredo é ato da trilha (RN-INT-16): só no clique, nunca ao abrir a tela. */
export function useRevealWebhookSecret() {
  const api = useApi()
  return useMutation({
    mutationFn: (webhookId: string) =>
      api.get<WebhookSecret>(`/admin/integrations/webhooks/${webhookId}/secret`),
  })
}

export function useDeliveries(id: string, params: { filter: DeliveryFilter; page: number }) {
  const api = useApi()
  return useQuery({
    queryKey: integrationKeys.deliveries(id, params),
    queryFn: () =>
      api.list<Delivery, DeliveriesMeta>(`/admin/integrations/${id}/deliveries`, {
        filter: params.filter,
        page: params.page,
        perPage: 25,
      }),
    placeholderData: (previous) => previous,
  })
}

export function useRetryDelivery(integrationId: string) {
  const api = useApi()
  return useWebhookMutation(integrationId, (deliveryId: string) =>
    api.post<Delivery>(`/admin/integrations/deliveries/${deliveryId}/retry`),
  )
}
